import { useState, useEffect } from 'react';
import { supabase } from '../lib/constants';

const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

export default function HomeEditor({ onClose, isMobile }) {
  const [tab, setTab]           = useState('news'); // news | oncall | activity | inspiration
  const [news, setNews]         = useState([]);
  const [oncall, setOncall]     = useState({});
  const [activity, setActivity] = useState({ title:'', event_date:'', event_time:'', event_location:'', event_topic:'' });
  const [newNews, setNewNews]   = useState({ body:'', author:'' });
  const [fellows, setFellows]   = useState([]);
  const [saving, setSaving]     = useState(false);
  const [saved, setSaved]       = useState('');
  const [customInspo, setCustomInspo] = useState({ title:'', body:'' });

  useEffect(() => {
    fetchContent();
    supabase.from('profiles').select('full_name').eq('role','fellow').then(({ data }) => setFellows(data || []));
  }, []);

  const fetchContent = async () => {
    const { data } = await supabase.from('homepage_content').select('*').eq('is_active', true).order('display_order');
    if (data) {
      setNews(data.filter(d => d.content_type === 'news'));
      const oncallMap = {};
      data.filter(d => d.content_type === 'oncall').forEach(o => { oncallMap[o.day_of_week] = o; });
      setOncall(oncallMap);
      const act = data.find(d => d.content_type === 'next_activity');
      if (act) setActivity({ id: act.id, title: act.title||'', event_date: act.event_date||'', event_time: act.event_time||'', event_location: act.event_location||'', event_topic: act.event_topic||'' });
    }
  };

  const notify = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 2500); };

  const addNews = async () => {
    if (!newNews.body.trim()) return;
    setSaving(true);
    await supabase.from('homepage_content').insert({ content_type:'news', body: newNews.body, author: newNews.author || 'Program Director', is_active: true, display_order: Date.now() });
    setNewNews({ body:'', author:'' });
    await fetchContent();
    setSaving(false); notify('✓ News posted!');
  };

  const deleteNews = async (id) => {
    await supabase.from('homepage_content').update({ is_active: false }).eq('id', id);
    await fetchContent(); notify('✓ Removed');
  };

  const saveOncall = async () => {
    setSaving(true);
    for (const day of DAYS) {
      const entry = oncall[day];
      if (entry?.id) {
        await supabase.from('homepage_content').update({ fellow_name: entry.fellow_name }).eq('id', entry.id);
      } else {
        await supabase.from('homepage_content').insert({ content_type:'oncall', day_of_week: day, fellow_name: oncall[day]?.fellow_name || 'TBD', is_active: true, display_order: DAYS.indexOf(day)+1 });
      }
    }
    await fetchContent(); setSaving(false); notify('✓ On-call schedule saved!');
  };

  const saveActivity = async () => {
    setSaving(true);
    if (activity.id) {
      await supabase.from('homepage_content').update({ title: activity.title, event_date: activity.event_date || null, event_time: activity.event_time, event_location: activity.event_location, event_topic: activity.event_topic, updated_at: new Date().toISOString() }).eq('id', activity.id);
    } else {
      await supabase.from('homepage_content').insert({ content_type:'next_activity', title: activity.title, event_date: activity.event_date || null, event_time: activity.event_time, event_location: activity.event_location, event_topic: activity.event_topic, is_active: true, display_order: 1 });
    }
    await fetchContent(); setSaving(false); notify('✓ Activity saved!');
  };

  const addCustomInspo = async () => {
    if (!customInspo.body.trim()) return;
    setSaving(true);
    await supabase.from('homepage_content').insert({ content_type:'inspiration', title: customInspo.title || 'Program Director', body: customInspo.body, is_active: true, display_order: 99 });
    setCustomInspo({ title:'', body:'' });
    setSaving(false); notify('✓ Quote added!');
  };

  const tabs = [
    { id:'news',        label:'📢 News' },
    { id:'oncall',      label:'📅 On-Call' },
    { id:'activity',    label:'🗓 Next Activity' },
    { id:'inspiration', label:'✨ Inspiration' },
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:14, width:'100%', maxWidth:560, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>

        {/* Header */}
        <div style={{ background:'#1A3A5C', padding:'16px 20px', borderRadius:'14px 14px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:'#F5F0E8' }}>✎ Edit Home Page</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#7EB8D4', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #F0EDE8', background:'#FAFAF8' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ flex:1, padding:'11px 6px', border:'none', background:'transparent', fontSize:12, fontWeight: tab===t.id?700:500, color: tab===t.id?'#1A3A5C':'#888', cursor:'pointer', fontFamily:"'DM Sans'", borderBottom: tab===t.id?'2px solid #1A3A5C':'2px solid transparent' }}>
              {t.label}
            </button>
          ))}
        </div>

        {saved && <div style={{ background:'#F0FFF6', border:'1px solid #A8DCC0', color:'#1E5C40', padding:'8px 16px', fontSize:13, fontWeight:600 }}>{saved}</div>}

        <div style={{ flex:1, overflowY:'auto', padding:'18px 20px' }}>

          {/* NEWS TAB */}
          {tab === 'news' && (
            <div>
              <div style={es.sectionTitle}>Post New Announcement</div>
              <div style={es.field}><label style={es.label}>Message</label>
                <textarea value={newNews.body} rows={3} placeholder="Type your announcement…" onChange={e => setNewNews(n => ({...n, body: e.target.value}))} style={es.textarea} />
              </div>
              <div style={es.field}><label style={es.label}>Posted by (optional)</label>
                <input value={newNews.author} placeholder="Dr. Fadi Abu-Farsakh" onChange={e => setNewNews(n => ({...n, author: e.target.value}))} style={es.input} />
              </div>
              <button onClick={addNews} disabled={saving || !newNews.body.trim()} style={{ ...es.primaryBtn, marginBottom:20 }}>
                {saving ? 'Posting…' : '📢 Post Announcement'}
              </button>

              <div style={es.sectionTitle}>Current Announcements</div>
              {news.length === 0
                ? <div style={es.empty}>No announcements posted yet.</div>
                : news.map(n => (
                  <div key={n.id} style={{ background:'#F7F4F0', borderRadius:8, padding:'10px 12px', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:'#333', lineHeight:1.5 }}>{n.body}</div>
                      <div style={{ fontSize:10, color:'#888', marginTop:4 }}>📌 {n.author} · {new Date(n.created_at).toLocaleDateString()}</div>
                    </div>
                    <button onClick={() => deleteNews(n.id)} style={{ background:'#FFF0F0', border:'1px solid #F4A8A8', color:'#C42A2A', borderRadius:6, padding:'4px 10px', fontSize:11, cursor:'pointer', flexShrink:0 }}>Remove</button>
                  </div>
                ))}
            </div>
          )}

          {/* ON-CALL TAB */}
          {tab === 'oncall' && (
            <div>
              <div style={es.sectionTitle}>Weekly On-Call Schedule</div>
              <p style={{ fontSize:12, color:'#888', marginBottom:16 }}>Select the fellow on call for each day of the week.</p>
              {DAYS.map(day => (
                <div key={day} style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:'#1A3A5C', width:36 }}>{day}</span>
                  <select value={oncall[day]?.fellow_name || 'TBD'}
                    onChange={e => setOncall(prev => ({ ...prev, [day]: { ...prev[day], fellow_name: e.target.value } }))}
                    style={{ ...es.input, flex:1, marginBottom:0 }}>
                    <option value='TBD'>TBD</option>
                    {fellows.map(f => <option key={f.full_name} value={f.full_name}>{f.full_name}</option>)}
                  </select>
                </div>
              ))}
              <button onClick={saveOncall} disabled={saving} style={es.primaryBtn}>
                {saving ? 'Saving…' : '💾 Save On-Call Schedule'}
              </button>
            </div>
          )}

          {/* NEXT ACTIVITY TAB */}
          {tab === 'activity' && (
            <div>
              <div style={es.sectionTitle}>Next Activity Details</div>
              <div style={es.field}><label style={es.label}>Activity Name *</label>
                <input value={activity.title} placeholder="e.g. Journal Club" onChange={e => setActivity(a => ({...a, title: e.target.value}))} style={es.input} />
              </div>
              <div style={es.field}><label style={es.label}>Date</label>
                <input type="date" value={activity.event_date} onChange={e => setActivity(a => ({...a, event_date: e.target.value}))} style={es.input} />
              </div>
              <div style={es.field}><label style={es.label}>Time</label>
                <input value={activity.event_time} placeholder="e.g. 3:00 PM – 4:00 PM" onChange={e => setActivity(a => ({...a, event_time: e.target.value}))} style={es.input} />
              </div>
              <div style={es.field}><label style={es.label}>Location</label>
                <input value={activity.event_location} placeholder="e.g. Conference Room B, KHCC" onChange={e => setActivity(a => ({...a, event_location: e.target.value}))} style={es.input} />
              </div>
              <div style={es.field}><label style={es.label}>Topic / Description</label>
                <input value={activity.event_topic} placeholder="e.g. Opioid rotation in renal failure" onChange={e => setActivity(a => ({...a, event_topic: e.target.value}))} style={es.input} />
              </div>
              <button onClick={saveActivity} disabled={saving || !activity.title.trim()} style={es.primaryBtn}>
                {saving ? 'Saving…' : '💾 Save Activity'}
              </button>
            </div>
          )}

          {/* INSPIRATION TAB */}
          {tab === 'inspiration' && (
            <div>
              <div style={es.sectionTitle}>Add Custom Quote</div>
              <p style={{ fontSize:12, color:'#888', marginBottom:14 }}>The quote rotates daily. Built-in quotes are already included — add your own here.</p>
              <div style={es.field}><label style={es.label}>Quote / Message</label>
                <textarea value={customInspo.body} rows={3} placeholder="Type an inspirational quote…" onChange={e => setCustomInspo(q => ({...q, body: e.target.value}))} style={es.textarea} />
              </div>
              <div style={es.field}><label style={es.label}>Attribution (author/source)</label>
                <input value={customInspo.title} placeholder="e.g. Cicely Saunders" onChange={e => setCustomInspo(q => ({...q, title: e.target.value}))} style={es.input} />
              </div>
              <button onClick={addCustomInspo} disabled={saving || !customInspo.body.trim()} style={es.primaryBtn}>
                {saving ? 'Adding…' : '✨ Add Quote'}
              </button>
            </div>
          )}
        </div>

        <div style={{ padding:'12px 20px', borderTop:'1px solid #F0EDE8' }}>
          <button onClick={onClose} style={{ width:'100%', padding:'10px', background:'#EEE', border:'none', borderRadius:8, fontSize:13, cursor:'pointer', fontFamily:"'DM Sans'" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

const es = {
  sectionTitle: { fontFamily:"'Cormorant Garamond',serif", fontSize:16, color:'#1A3A5C', marginBottom:12, fontWeight:600 },
  field: { marginBottom:14 },
  label: { display:'block', fontSize:10, fontWeight:700, color:'#555', marginBottom:5, letterSpacing:'0.5px', textTransform:'uppercase' },
  input: { width:'100%', padding:'10px 12px', border:'1.5px solid #E0DDD8', borderRadius:8, fontSize:13, color:'#1A1A1F', background:'#FAFAF8', outline:'none', fontFamily:"'DM Sans'", boxSizing:'border-box', marginBottom:0 },
  textarea: { width:'100%', padding:'10px 12px', border:'1.5px solid #E0DDD8', borderRadius:8, fontSize:13, color:'#1A1A1F', background:'#FAFAF8', resize:'vertical', outline:'none', fontFamily:"'DM Sans'", lineHeight:1.6, boxSizing:'border-box' },
  primaryBtn: { width:'100%', padding:'11px', background:'#1A3A5C', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans'" },
  empty: { fontSize:13, color:'#aaa', fontStyle:'italic', textAlign:'center', padding:'16px 0' },
};
